import type { Editor } from "@tiptap/core";
import type { Node as PmNode } from "@tiptap/pm/model";
import { TextSelection } from "@tiptap/pm/state";

interface CellLocation {
	table: PmNode;
	tablePos: number;
	rowIndex: number;
	cellIndex: number;
}

class TableOps {
	next(editor: Editor) {
		const location = this.locate(editor);

		if (location === null) {
			return false;
		}

		const row = location.table.child(location.rowIndex);

		if (location.cellIndex + 1 < row.childCount) {
			return this.selectCell(editor, location, location.rowIndex, location.cellIndex + 1);
		}

		if (location.rowIndex + 1 < location.table.childCount) {
			return this.selectCell(editor, location, location.rowIndex + 1, 0);
		}

		return this.insertRow(editor, location, location.table.childCount);
	}

	previous(editor: Editor) {
		const location = this.locate(editor);

		if (location === null) {
			return false;
		}

		if (location.cellIndex > 0) {
			return this.selectCell(editor, location, location.rowIndex, location.cellIndex - 1);
		}

		if (location.rowIndex > 0) {
			const previousRow = location.table.child(location.rowIndex - 1);

			return this.selectCell(editor, location, location.rowIndex - 1, previousRow.childCount - 1);
		}

		return true;
	}

	rowBelow(editor: Editor) {
		const location = this.locate(editor);

		if (location === null) {
			return false;
		}

		return this.insertRow(editor, location, location.rowIndex + 1);
	}

	appendRow(editor: Editor, tablePos: number) {
		const table = editor.state.doc.nodeAt(tablePos);

		if (table === null || table.type.name !== "table") {
			return;
		}

		this.insertRow(editor, { table, tablePos, rowIndex: 0, cellIndex: 0 }, table.childCount);
		editor.view.focus();
	}

	backspace(editor: Editor) {
		const { $from, empty } = editor.state.selection;

		if (!empty || $from.parentOffset !== 0 || $from.parent.content.size > 0) {
			return false;
		}

		const location = this.locate(editor);

		if (location === null) {
			return false;
		}

		const row = location.table.child(location.rowIndex);

		if (!this.rowEmpty(row)) {
			if (location.cellIndex > 0) {
				return this.cursorToCellEnd(editor, location, location.rowIndex, location.cellIndex - 1);
			}

			if (location.rowIndex > 0) {
				const previousRow = location.table.child(location.rowIndex - 1);

				return this.cursorToCellEnd(
					editor,
					location,
					location.rowIndex - 1,
					previousRow.childCount - 1,
				);
			}

			return false;
		}

		if (location.table.childCount === 1) {
			const paragraph = editor.state.schema.nodes.paragraph.create();
			const tr = editor.state.tr.replaceWith(
				location.tablePos,
				location.tablePos + location.table.nodeSize,
				paragraph,
			);

			tr.setSelection(TextSelection.create(tr.doc, location.tablePos + 1));
			editor.view.dispatch(tr.scrollIntoView());

			return true;
		}

		const start = this.rowStart(location.tablePos, location.table, location.rowIndex);
		const tr = editor.state.tr.delete(start, start + row.nodeSize);

		tr.setSelection(TextSelection.near(tr.doc.resolve(start), location.rowIndex > 0 ? -1 : 1));
		editor.view.dispatch(tr.scrollIntoView());

		return true;
	}

	removeCursorRow(editor: Editor, tablePos: number) {
		const table = editor.state.doc.nodeAt(tablePos);

		if (table === null || table.childCount <= 1) {
			return;
		}

		const cursor = this.locate(editor);
		const rowIndex =
			cursor !== null && cursor.tablePos === tablePos ? cursor.rowIndex : table.childCount - 1;
		const start = this.rowStart(tablePos, table, rowIndex);
		const row = table.child(rowIndex);

		editor.view.dispatch(editor.state.tr.delete(start, start + row.nodeSize).scrollIntoView());
		editor.view.focus();
	}

	private locate(editor: Editor): CellLocation | null {
		const { $from } = editor.state.selection;

		for (let level = $from.depth; level >= 3; level -= 1) {
			if ($from.node(level).type.name !== "tableCell") {
				continue;
			}

			return {
				table: $from.node(level - 2),
				tablePos: $from.before(level - 2),
				rowIndex: $from.index(level - 2),
				cellIndex: $from.index(level - 1),
			};
		}

		return null;
	}

	private rowStart(tablePos: number, table: PmNode, rowIndex: number) {
		let pos = tablePos + 1;

		for (let index = 0; index < rowIndex; index += 1) {
			pos += table.child(index).nodeSize;
		}

		return pos;
	}

	private insertRow(editor: Editor, location: CellLocation, rowIndex: number) {
		const reference = location.table.child(
			Math.max(0, Math.min(rowIndex - 1, location.table.childCount - 1)),
		);
		const cells = Array.from({ length: reference.childCount }, () =>
			editor.state.schema.nodes.tableCell.create(),
		);
		const row = editor.state.schema.nodes.tableRow.create(null, cells);
		const pos = this.rowStart(location.tablePos, location.table, rowIndex);
		const tr = editor.state.tr.insert(pos, row);

		tr.setSelection(TextSelection.create(tr.doc, pos + 2));
		editor.view.dispatch(tr.scrollIntoView());

		return true;
	}

	private rowEmpty(row: PmNode) {
		for (let index = 0; index < row.childCount; index += 1) {
			if (row.child(index).content.size > 0) {
				return false;
			}
		}

		return true;
	}

	private cellStart(location: CellLocation, rowIndex: number, cellIndex: number) {
		const row = location.table.child(rowIndex);
		let pos = this.rowStart(location.tablePos, location.table, rowIndex) + 1;

		for (let index = 0; index < cellIndex; index += 1) {
			pos += row.child(index).nodeSize;
		}

		return pos;
	}

	private selectCell(editor: Editor, location: CellLocation, rowIndex: number, cellIndex: number) {
		const cell = location.table.child(rowIndex).child(cellIndex);
		const pos = this.cellStart(location, rowIndex, cellIndex);
		const tr = editor.state.tr.setSelection(
			TextSelection.create(editor.state.doc, pos + 1, pos + 1 + cell.content.size),
		);

		editor.view.dispatch(tr.scrollIntoView());

		return true;
	}

	private cursorToCellEnd(
		editor: Editor,
		location: CellLocation,
		rowIndex: number,
		cellIndex: number,
	) {
		const cell = location.table.child(rowIndex).child(cellIndex);
		const pos = this.cellStart(location, rowIndex, cellIndex);
		const tr = editor.state.tr.setSelection(
			TextSelection.create(editor.state.doc, pos + 1 + cell.content.size),
		);

		editor.view.dispatch(tr.scrollIntoView());

		return true;
	}
}

export const DocumentTable = new TableOps();
